import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { Button, Loader } from 'ncoded-component-library';
import DownloadIcon from 'icons/Download.icon';
import SelectField from 'components/SelectField';
import RadioGroupField from 'components/RadioGroupField';
import CheckboxFieldGroup from 'components/CheckboxFieldGroup';
import useAnalyticsExportOptions from 'hooks/useAnalyticsExportOptions';
import {
  createRecordFromKeys,
  getArrayLength,
  getCheckedValuesInObject,
} from 'utils';
import { DATA_ROOM_ARCHIVE_FILE_TYPE } from 'router/subrouters/DataRoom/subrouters/Settings/pages/SetupPage/components/Snapshots/types';
import { ANALYTICS_EXPORT_TIMEFRAME } from 'router/subrouters/DataRoom/subrouters/Reports/pages/Reports/types';
import { TIMEFRAME_COPY } from 'router/subrouters/DataRoom/subrouters/Reports/pages/Overview/components/DownloadReportModal/components/DownloadReportForm/data';
import {
  BUYERS_ACTIVITY_TRACK_TYPE,
  BuyersActivityTrackType,
  MemberActivityExportPage,
} from 'models/Lead';
import DownloadStatusContext from 'providers/DownloadStatus/DownloadStatus.context';
import api from 'api';
import downloadService from 'services/downloadService';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import {
  MEMBER_ACTIVITY_EVENT_TITLE_COPY,
  MEMBER_ACTIVITY_EVENT_DESCRIPTION_COPY,
} from './data';

import './ExportMemberActivityForm.styles.scss';

type ExportMemberActivityFormProps = {
  className?: string;
  leadId: string;
  dataRoomDisabled?: boolean;
  memberActivityPage: MemberActivityExportPage;
  onCancel: () => void;
};

const ExportMemberActivityForm: React.FC<ExportMemberActivityFormProps> = (
  props,
) => {
  const { className, leadId, dataRoomDisabled, memberActivityPage, onCancel } =
    props;

  const classes = classNames('export-member-activity-form', className);

  const { t } = useTranslation();

  const { startLeadAnalyticsExportDownload, failLeadAnalyticsExportDownload } =
    useContext(DownloadStatusContext);

  const getMixpanelProps = useGetMixpanelProps();

  const trackTypes = useMemo(
    () => Object.values(BUYERS_ACTIVITY_TRACK_TYPE),
    [],
  );

  const initialValues = useMemo(
    () => ({
      timeframe: ANALYTICS_EXPORT_TIMEFRAME.OVERALL,
      format: DATA_ROOM_ARCHIVE_FILE_TYPE.PDF,
      sections: {
        ...createRecordFromKeys(trackTypes, true),
        ...(dataRoomDisabled && {
          [BUYERS_ACTIVITY_TRACK_TYPE.DATA_ROOM]: false,
        }),
      },
    }),
    [trackTypes, dataRoomDisabled],
  );

  const { fileTypeOptions, timeframeOptions } = useAnalyticsExportOptions();

  const eventOptions = useMemo(
    () =>
      trackTypes.map((value) => {
        const isDataRoomDisabled =
          dataRoomDisabled && value === BUYERS_ACTIVITY_TRACK_TYPE.DATA_ROOM;

        return {
          value,
          enabled: !isDataRoomDisabled,
          tooltip: isDataRoomDisabled && t('noDataRoomAccessTooltip'),
          label: (
            <span className="export-form__section-option">
              <span className="export-form__section-title">
                {t(MEMBER_ACTIVITY_EVENT_TITLE_COPY[value])}
              </span>
              <span className="export-form__section-description">
                {t(MEMBER_ACTIVITY_EVENT_DESCRIPTION_COPY[value])}
              </span>
            </span>
          ),
        };
      }),
    [trackTypes, dataRoomDisabled, t],
  );

  const onSubmit = useCallback(
    async ({ timeframe, format, sections }) => {
      const selectedSections = getCheckedValuesInObject(
        sections,
      ) as BuyersActivityTrackType[];

      try {
        startLeadAnalyticsExportDownload(
          t('exportReportSummary', {
            count: getArrayLength(selectedSections),
            timeframe: t(TIMEFRAME_COPY[timeframe]),
            format: t(format),
          }),
          format === DATA_ROOM_ARCHIVE_FILE_TYPE.PDF,
        );

        await api.portfolio.seller.exportMemberActivity(leadId, {
          format,
          timeframe,
          sections: selectedSections,
          uniqueId: downloadService.downloadUniqueId,
          page: memberActivityPage,
        });

        onCancel();
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
        failLeadAnalyticsExportDownload();
      }
    },
    [
      leadId,
      memberActivityPage,
      failLeadAnalyticsExportDownload,
      getMixpanelProps,
      onCancel,
      startLeadAnalyticsExportDownload,
      t,
    ],
  );

  return (
    <Form
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ submitting, handleSubmit }) => (
        <form onSubmit={handleSubmit} className={classes}>
          {submitting && <Loader />}

          <Field
            name="timeframe"
            required
            label={t('period')}
            component={SelectField}
            options={timeframeOptions}
          />

          <RadioGroupField
            label={t('chooseFileType')}
            required
            name="format"
            options={fileTypeOptions}
          />

          <CheckboxFieldGroup
            name="sections"
            required
            label={t('informationToExport')}
            options={eventOptions}
          />

          <div className="actions">
            <Button variant="link" onClick={onCancel}>
              {t('cancel')}
            </Button>

            <Button type="submit" icon={DownloadIcon} disabled={submitting}>
              {t('export')}
            </Button>
          </div>
        </form>
      )}
    />
  );
};

export default ExportMemberActivityForm;
